/* eslint-disable prettier/prettier */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';

import {
  ActionItem,
  FeedEvent,
  LeaseLifecycle,
  Process,
  Unit,
  UserData,
} from '@urbanmix-tech/shared-js';

import {
  convertPseudoDateToRealDate,
  getLongNumberStr,
} from '@/helpers/common';
import { getFeedEvents } from '@/data';
import { unitStatusGroup } from '@/helpers/unitStatus';
import { selectUnitStatusById } from '@/features/project/unitStatusSelectors';

import {
  ITableCell,
  ITableRow,
  VirtualizedTable,
  ColumnSeletion,
} from '@/components/shared/Table';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import {
  setHighlightedUnits,
  setSelectedUnit,
  setApartmentData,
  setVisualizer,
  setHoveredUnitId,
  setBuildingVisual,
} from '@/features/project/projectSlice';
import {
  ColorGradeVisualizer,
  ColorGradientVisualizer,
  Visualizer,
} from '@urbanmix-tech/um-embedded';

import { CategoryGradientVisualizer, GenericDistinctVisualizer, colorScales } from './BuildingVisualizers';
import type { ColorScaleOptions } from './BuildingVisualizers';
import { LeaseCard } from './LeaseCard';
import { DisplayProcess } from './ExpiringLeaseItem';
import { AggregatedData, ExpiringLeasesAggregation, UnitData } from './ExpiringLeasesAggrigation';
import { analyzeUnitProcesses, steps, stepToStatus } from './expiringLeasesUtils';
import { OccupancyStatsHeader } from './OccupancyStatsHeader';

let stepsNum: Record<number, string> = {};

for (const key in steps) {
  const value = steps[key];
  stepsNum[value] = key;
}


const aggregator = (units : Unit[],categories: number[]): AggregatedData => {
  const data = units.map((unit: Unit) => {
    let endDate = convertPseudoDateToRealDate(unit.leaseEndDate) ?? new Date();
    // console.log(
    //   'endDate',
    //   endDate,
    //   (endDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    // );
    return {
      daysToExpire: Math.ceil(
        (endDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)),
      unit,
      type: unit.affordable ? 'affordable' : 'market',
    };
  });

  let groups: Record<string, {
    units: UnitData[];
    category: number;
  }> = {};

  let keys = categories.map((days) => {
    return `${days}`;
  });

  keys.push('rest');

  data.forEach((unitData: any) => {
    // console.log('unitData',unitData)
    let category = categories.find((days) => unitData.daysToExpire <= days) ?? categories[0];
    let key = category
      ? `${category}`
      : 'rest';
      
    
    if (!groups[key]) {
      groups[key] = {
        units: [],
        category: category,
      };
    }
    groups[key]!.units.push(unitData);
    groups[key]!.category = category;
  });

  return {
    grouped: groups,
    categories: categories,    
    keys
  };
}

type ExpiringLeasesTabContentProps = {
  units: Unit[];
  actionItems: ActionItem[];
  hoveredUnitId: string | null;
  onSelectUnit(unitId: string | null): void;
  onHoverUnit(unitId: string | null): void;
};

// Extended row type to include lease/process data for LeaseCard
interface ExpiringLeaseRow extends ITableRow {
  displayProcess: DisplayProcess[];
  daysToExpire: number;
  expirationDate: Date | undefined;
}

const STORAGE_KEY = 'expiringLeasesFilters';

interface StoredFilters {
  expiresInDays: number;
  showAffordable: boolean;
  showMarket: boolean;
  selectedCategoryKeys: string[];
}

const defaultFilters: StoredFilters = {
  expiresInDays: 99999,
  showAffordable: true,
  showMarket: true,
  selectedCategoryKeys: ['0', '20', '30', '40', '60', '90', '120', 'rest'],
};

function loadFilters(): StoredFilters {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...defaultFilters, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.warn('Failed to load expiring leases filters:', e);
  }
  return defaultFilters;
}

function saveFilters(filters: StoredFilters): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch (e) {
    console.warn('Failed to save expiring leases filters:', e);
  }
}

export function ExpiringLeasesTabContent({
  units,
  actionItems,
  hoveredUnitId,
  onSelectUnit,
  onHoverUnit,
}: ExpiringLeasesTabContentProps) {
  const ref = useRef<HTMLDivElement>(null);
  const filtersLoaded = useRef(false);

  const [filters, setFilters] = useState<StoredFilters | null>(null);
  const [feedEvents, setFeedEvents] = useState<FeedEvent[]>([]);

  
  // Load filters from localStorage on mount
  useEffect(() => {
    const stored = loadFilters();
    console.log('&& setting loaded stored filters', stored);
    setFilters(stored);
    filtersLoaded.current = true;
  }, []);

  // Persist filters when they change (only after initial load)
  useEffect(() => {
    if (!filtersLoaded.current) {
      return;
    }
    saveFilters(filters || defaultFilters);
  }, [filters]);

  // Mount Redux dispatcher
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchEvents = async () => {
      if (units.length > 0) {
        const events = await getFeedEvents({projectId: units[0].projectId});
        setFeedEvents(events);
      }
    }
    fetchEvents();
  },[units]);

  const handleClickRow = (apt: any) => {
    console.log('handle click row', apt);
    dispatch(setSelectedUnit({ apartmentId: apt.id }));
  };

  const processes : Process[] = useSelector((state: any) => state.project.processes);
  const allLeases = useSelector((state: any) => state.project.projectLeases);
  const statusById = useSelector(selectUnitStatusById);

  // Create a map of units that have draft leases and active leases by unitId
  const { unitsDraftLeaseMap, activeLeaseByUnitId } = useMemo(() => {
    if (!allLeases) return { unitsDraftLeaseMap: new Set<string>(), activeLeaseByUnitId: new Map<string, any>() };
    const draftUnitIds = new Set<string>();
    const activeLeasesMap = new Map<string, any>();

    allLeases.forEach((lease: any) => {
      if (lease.status === 'draft') {
        draftUnitIds.add(lease.unitId);
      }
      // Get active lease (valid status, most recent by end date)
      if (lease.status === 'valid') {
        const existing = activeLeasesMap.get(lease.unitId);
        if (!existing || (lease.leaseEndDate > existing.leaseEndDate)) {
          activeLeasesMap.set(lease.unitId, lease);
        }
      }
    });
    return { unitsDraftLeaseMap: draftUnitIds, activeLeaseByUnitId: activeLeasesMap };
  }, [allLeases]);

  const columns: ITableCell[] = useMemo(
    () => [
      {
        label: 'Unit',
        sortable: true,
        className: 'bg-gray-950',
      },
      {
        label: 'Type',
        sortable: true,
        className: 'bg-gray-950',
        showCheckbox: false,
      },
      {
        label: 'Lease Expires',
        sortable: true,
        className: 'bg-gray-950 text-right',
        showCheckbox: false,
      },
      {
        label: 'Days',
        sortable: true,
        className: 'bg-gray-950',
        showCheckbox: false,
      },
      {
        label: 'Rent',
        sortable: true,
        className: 'bg-gray-950',
        showCheckbox: false,
      },
      {
        label: 'Sqft',
        sortable: true,
        className: 'bg-gray-950',
        showCheckbox: false,
      },
      {
        label: 'Annual Rent/Sqft',
        sortable: true,
        className: 'bg-gray-950 text-right',
        showCheckbox: false,
      },
      {
        label: 'Status',
        sortable: true,
        className: 'bg-gray-950',
        showCheckbox: false,
      },
    ],
    []
  );

  let categories = [0, 20, 30, 40, 60, 90, 120];

  

  const filteredUnits = useMemo(() => {
    return units.filter((unit) => {
      let endDate = convertPseudoDateToRealDate(unit.leaseEndDate);

      // if end data is in expiringInDays days
      if (endDate) {
        let today = new Date();
        let diff = Math.ceil(
          (endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
        );
        // console.log('expiring diff',unit.id, endDate,  diff);
        if (diff <= (filters?.expiresInDays || 0) && diff >= 0) {
          if (unit.affordable && (filters?.showAffordable || false)) {
            return true;
          } else if (!unit.affordable && (filters?.showMarket || false)) {
            return true;
          }
        } else {
          return false;
        }
      }
      return false;
    });
  }, [units, filters]);

  const aggregatedData = useMemo(() => {
    console.log('aggregated date changed')
    return aggregator(filteredUnits, categories);
  }, [filteredUnits]); 

  const rows: ExpiringLeaseRow[] = useMemo(() => {    
    
    let units : Unit[] = []
    
    if ((filters?.selectedCategoryKeys || []).length == 0) {
      units = filteredUnits
    } else {
      let unitsData : UnitData[] = Array<UnitData>();

      (filters?.selectedCategoryKeys || []).forEach((key : string) => {
        if (aggregatedData.grouped[key]) {
          unitsData = unitsData.concat(aggregatedData.grouped[key].units)
        }
      });

      units = unitsData.map((unitData) => {
        return unitData.unit;
      })
    }

    const unitsData = units.map((unit) => {      
      let unitActionItems = actionItems.filter((actionItem) => {
        return actionItem.unitId === unit.id;
      });

      const lastStep = unitActionItems.reduce((acc, item) => {
        if (
          item.response &&
          item.response.event &&
          steps[item.response.event] &&
          item.status === 'done'
        ) {
          return Math.max(acc, steps[item.response.event]);
        } else {
          return acc;
        }
      }, 0);

      const firstDate = unitActionItems.reduce((acc, item) => {
        const d = convertPseudoDateToRealDate(item.createDate);
        if (
          item.response &&
          item.response.event &&
          steps[item.response.event] &&
          (d?.getTime() ?? Infinity) > 0
        ) {
          return Math.min(acc, d?.getTime() ?? Infinity);
        }
        return acc;
      }, Infinity);

      let lastDate = unitActionItems.reduce((acc, item) => {
        if (
          item.response &&
          item.response.event &&
          steps[item.response.event] &&
          item.status === 'done'
        ) {
          //@ts-ignore
          return Math.max(acc, item.updateDate.seconds);
        } else return acc;
      }, 0);

      let sortedItems = unitActionItems.sort((a, b) => {
        //@ts-ignore
        return a.createDate.seconds - b.createDate.seconds;
      });

      let termsItem = sortedItems.reverse().find((item) => {
        return item.response && item.response.leaseTerms;
      });

      if (lastStep < 4) {
        // set last date to now in unix timestmap
        lastDate = Math.floor(Date.now() / 1000);
      }

      // convert seconds to days
      // how many days until unit expires according to convertPseudoDateToRealDate(unit.leaseEndDate)

      const endDate = convertPseudoDateToRealDate(unit.leaseEndDate);
      const days = endDate ? Math.ceil((
        endDate.getTime() -
          Date.now()) /
          (1000 * 60 * 60 * 24)): Infinity;

      // Unit status now comes from the `state_*` feed rather than the legacy
      // action-item step map, which only ever described the renewal flow.
      const unitStatus = statusById[unit.id];
      const lastStatus = unitStatus ? unitStatusGroup(unitStatus) : stepToStatus[lastStep];

      // Get unit processes and analyze them
      const unitProcesses = processes.filter((p) => p.unitId === unit.id);
      const displayProcess = analyzeUnitProcesses(unit, unitProcesses, feedEvents, actionItems);
      const completedProcess = displayProcess.find((p) => p.process.status === 'completed');

      // console.log('base unit', unit);
      return {
        id: unit.id,
        currentState: lastStatus,
        started: firstDate,
        ended: lastDate,
        expires: convertPseudoDateToRealDate(unit.leaseEndDate),
        days: days,
        actions: unitActionItems.length,
        type: unit.affordable ? 'affordable' : 'market',
        rent: unit.actualRent ?? 0,
        sqft: unit.sqft ?? 0,
        unit: unit,
        displayProcess: displayProcess,
        completedProcess: completedProcess,
      };
    });

    // console.log('unitsData', unitsData);

    return unitsData
      .sort((a, b) => {
        return a.days - b.days;
      })
      .map((unit) => {
      const data = {
        Status: {
          kind: 'string',
          label: unit.currentState,
          value: unit.currentState,
          className: 'border-l border-zinc-600',
        },
        Unit: {
          label: unit.id,
          value: unit.id,
          className: 'border-l border-r border-zinc-600',
        },
        Rent: {
          label: `$${getLongNumberStr(unit.rent || 0)}`,
          value: unit.rent,
          className: 'border-r border-zinc-600 justify-end',
        },
        Type: {
          label: unit.type,
          value: unit.type,
          className: 'border-r border-zinc-600',
        },
        Days: {
          label: `${unit.days}`,
          value: unit.days,
          className: 'border-r border-zinc-600 justify-end',
        },
        'Lease Expires': {
          label: unit.expires?.toLocaleDateString(),
          value: unit.expires,
          className: 'border-r border-zinc-600 justify-end px-2',
        },
        'Annual Rent/Sqft': {
          label: `$${(unit.rent / unit.sqft).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`,
          value: unit.rent / unit.sqft,
          className: 'border-r border-zinc-600 justify-end',
        },
        Sqft: {
          label: `${unit.sqft}`,
          value: unit.sqft,
          className: 'border-r border-zinc-600 justify-end',
        },
        Process: {
          label: unit.displayProcess.length > 0 ? unit.displayProcess[0].process.name : '',
          value: unit.displayProcess.length > 0 ? unit.displayProcess[0].process.name : ''
        },
        'Process Outcome': {
          label: unit.completedProcess ? unit.completedProcess.outcome : '',
          value: unit.completedProcess ? unit.completedProcess.outcome : ''
        },
        'Process Status': {
          label: unit.displayProcess.length > 0 ? unit.displayProcess[0].process.status : '',
          value: unit.displayProcess.length > 0 ? unit.displayProcess[0].process.status : ''
        },
        'Process Visual': {
          label: unit.completedProcess ? unit.completedProcess.completionEvent?.eventType ?? 'completed' : 'running',
          value: unit.completedProcess ? unit.completedProcess.completionEvent?.eventType ?? 'completed' : 'running',
        },
        'Process Last Event': {
          label: unit.displayProcess.length > 0 ? unit.displayProcess[0].lastActionItem?.title : '',
          value: unit.displayProcess.length > 0 ? unit.displayProcess[0].lastActionItem?.title : ''
        },
      } as Record<string, ITableCell>;

      // console.log('unit data', unit.id, data);
      return {
        id: unit.id,
        data,
        unit: unit.unit,
        displayProcess: unit.displayProcess,
        daysToExpire: unit.days,
        expirationDate: unit.expires,
      } as ExpiringLeaseRow;
    });
  }, [actionItems, units, filters, feedEvents, processes, statusById]);

  const [selectedScale, setSelectedScale] =
    useState<ColorScaleOptions>('default');
  const [selectedColumn, setSelectedColumn] = useState<ColumnSeletion | null>(
    null
  );

  let visualizer = useMemo(() => {
    let marketDays = new ColorGradeVisualizer(colorScales.market,10,120)
    let affordableDays = new ColorGradeVisualizer(colorScales.affordable,10,120)
    
    let combinedVisualizer = new CategoryGradientVisualizer({
      'affordable': affordableDays,
      'market': marketDays
    });

    return combinedVisualizer;
  },[selectedScale]);

  useEffect(() => {       
    const data = rows.map((row) => {
      return {
        apartmentId: row.data['Unit'].value,
        value: row.data['Type'].label + "_" + row.data['Days'].value,
        label: 'Type',
      };
    });

   
    dispatch(
      setBuildingVisual({
        apartmentData: data,
        visualizer: visualizer,
      })
    );

    //
    setTimeout(() => {
      dispatch(setHighlightedUnits(data));
    }, 500);
  }, [rows,visualizer]);

  const onCatgoriesChange = useCallback((keys : string[]) => {     
    console.log('selected new keys',keys)   
    setFilters((prev) => {
      return {
        ...prev!,
        selectedCategoryKeys: keys,
      }
    });    
  },[]);

  return (
    <div className='flex-1'>
    <div className="unit-mix h-[calc(100vh-112px)] w-full flex flex-col px-12" ref={ref}>
      <OccupancyStatsHeader />

      <div className="flex-1 overflow-auto">
        <div>
        <ExpiringLeasesAggregation
          tableData={aggregatedData}
          selectedCategoryKeys={filters?.selectedCategoryKeys || []}
          onCategoriesSelected={onCatgoriesChange}
          showMarket={filters?.showMarket}
          onShowMarketChange={(value) => setFilters((prev) => ({ ...prev!, showMarket: value }))}
          showAffordable={filters?.showAffordable}
          onShowAffordableChange={(value) => setFilters((prev) => ({ ...prev!, showAffordable: value }))}
        />
      </div>
        {(filters?.selectedCategoryKeys.length  || 0) > 0 && (
        <div className='flex flex-col gap-24 py-12'>
        {rows.map((row) => {
          // Get the active lease for this unit, or create a minimal lease from unit data
          const unitLease = activeLeaseByUnitId.get(row.id) || {
            id: `unit-${row.id}`,
            projectId: row.unit?.projectId || '',
            buildingId: row.unit?.buildingId || 0,
            unitId: row.id,
            leaseStartDate: row.unit?.leaseStartDate || '',
            leaseEndDate: row.unit?.leaseEndDate || '',
            monthlyRent: row.unit?.actualRent || 0,
            securityDeposit: 0,
            concessions: [],
            totalConcessionAmount: 0,
            status: 'valid',
            eLeaseFields: {},
            createdAt: new Date(),
            updatedAt: new Date(),
          };

          return (
            <LeaseCard
              key={row.id}
              unit={row.unit!}
              lease={unitLease}
              processes={processes}
              compact={true}
              showActions={true}
              onHoverUnit={(unitId) => {
                dispatch(setHoveredUnitId(unitId));
                onHoverUnit(unitId);
              }}
              onWorkflowEnd={() => {
                dispatch(setSelectedUnit({ apartmentId: row.id }));
              }}
              borderColorOverride={visualizer?.getColor ? visualizer.getColor(row.data['Type'].label + "_" + row.data['Days'].value) : undefined}
              hasDraftLease={unitsDraftLeaseMap.has(row.id)}
              displayProcess={row.displayProcess}
              daysToExpire={row.daysToExpire}
              expirationDate={row.expirationDate}
            />
          );
        })}
        </div>
        )}
      </div>    
    </div>
    </div>
  );
}


